/**
 * Resource nodes: the trees, the rocks and the ore, as things rather than as
 * paint.
 *
 * Until now the map's wealth was terrain. `Terrain.Forest` was a colour with a
 * `needsTerrain` gate on it, and a woodcutter's camp made timber out of the fact
 * that it stood on the right shade of green. Nothing was ever felled, nothing
 * ever ran out, and the ground a settlement had worked for an hour looked
 * exactly like the ground nobody had touched.
 *
 * A node is a discrete object standing on one tile: a kind, an amount remaining,
 * and the amount it started at. Workers walk to it, take a load off it, carry
 * that load home, and when it is empty it is **removed and the ground under it
 * reverts** — a felled wood becomes ash plain and a worked-out seam becomes ash
 * plain, which is honest in the hash, honest in the save and visible from the
 * camera.
 *
 * ── This file is data and nothing else ──────────────────────────────────────
 *
 * `src/data` may import only `src/sim/types.ts`, so everything here is a table:
 * which terrain a kind is seeded from, what it reverts to, what it yields, which
 * job works it, how much one node holds and how thickly they stand. The store,
 * the spatial index and the seeding walk are in `src/sim/systems/nodes.ts`; the
 * hauling is in `src/sim/systems/settlement.ts`.
 *
 * ── The two numbers that are the design ─────────────────────────────────────
 *
 * **`amount` and `lattice` are the brief's "smaller nodes for iron and stone".**
 * A tree is worth three times a rock and stands on a coarser lattice; rock and
 * ore are individually poorer and packed more thickly, so a quarry works a
 * *scatter* of rocks rather than one boulder and a patch visibly thins out as it
 * is worked. Measured on four seeds of a 384×384 map, the terrain pass paints
 * roughly 21,000–37,000 forest tiles, 21,000–37,000 stone and 4,000–6,400 iron;
 * at these lattices that is about 3,800 trees, 5,800 rocks and 1,000 ore —
 * eleven thousand nodes, which is why the store is columnar. See
 * `ResourceNodeStore`.
 *
 * ── Why the leyline is not a node ───────────────────────────────────────────
 *
 * `Terrain.Leyline` is the fourth economic terrain and it is deliberately absent
 * from this table. Two reasons, and the second is the load-bearing one. A
 * Wellspring's job is `Acolyte`, not a hauling job — nobody carries a leyline
 * home in a basket. And `scatterSeams` seeds leylines *last*, specifically so
 * the iron pass cannot paint over the only route to the second Age; a leyline
 * that could be *worked out* would reintroduce exactly the failure that guard
 * exists to prevent, on a longer fuse. Three patches a map is not a thing that
 * may be spent.
 */

import { Job, Terrain, type ResourceKey } from '../sim/types.ts';

export const NodeKind = {
  Tree: 0,
  Rock: 1,
  Ore: 2,
} as const;
export type NodeKindId = (typeof NodeKind)[keyof typeof NodeKind];

export interface NodeKindDef {
  kind: NodeKindId;
  id: string;
  /** Singular, for a tooltip: "Deadwood", "Boulder", "Ore Seam". */
  name: string;
  /** The terrain this kind is seeded from. */
  terrain: number;
  /** What the ground becomes when the last of it is taken. */
  revertTo: number;
  /** What a load off it becomes in the stores. */
  resource: ResourceKey;
  /** The job of the building that works it. See `harvestKind`. */
  job: number;
  /** How much one node holds, in resource units, before any multiplier. */
  amount: number;
  /**
   * One node per `lattice`×`lattice` block of matching terrain.
   *
   * The whole of "more numerous" — a smaller number is a thicker scatter. It is
   * a lattice rather than a per-tile chance so that seeding is a pure walk of
   * the terrain with no rolls in it: two worlds with the same map have the same
   * trees, in the same order, whatever the RNG has been used for elsewhere.
   */
  lattice: number;
  /**
   * How much a worker carries in one trip.
   *
   * The load is what makes distance matter, and it matters *inversely*: the
   * round trip costs the same seconds whatever is in the basket, so a small load
   * means more trips and a heavier tax on a badly placed camp. Ore is the
   * smallest load in the game for the same reason its nodes are the poorest.
   */
  load: number;
}

export const NODE_KINDS: NodeKindDef[] = [
  {
    kind: NodeKind.Tree,
    id: 'tree',
    name: 'Deadwood',
    terrain: Terrain.Forest,
    revertTo: Terrain.AshPlain,
    resource: 'timber',
    job: Job.Woodcutter,
    amount: 45,
    lattice: 3,
    load: 3,
  },
  {
    kind: NodeKind.Rock,
    id: 'rock',
    name: 'Outcrop',
    terrain: Terrain.StoneOutcrop,
    revertTo: Terrain.AshPlain,
    resource: 'stone',
    job: Job.Quarrier,
    /**
     * Richer per node than it was, because there are far fewer of them.
     *
     * Stone and ore used to stand on a **denser** lattice than wood — one every
     * 2×2 against timber's 3×3 — so a stone shelf came out as wall-to-wall
     * boulders and outnumbered the trees better than two to one (7,659 rocks
     * against 3,493 trees on a measured map). That is the opposite of what a
     * quarry should look like. The lattice is now the sparsest of the three and
     * the amount is raised to keep a *patch* worth roughly what it was worth,
     * so the change is to the picture and to the walking, not to the economy:
     * a quarry still finds stone, it just has to reach for it.
     */
    amount: 118,
    lattice: 8,
    load: 2,
  },
  {
    kind: NodeKind.Ore,
    id: 'ore',
    name: 'Ore Seam',
    terrain: Terrain.IronSeam,
    revertTo: Terrain.AshPlain,
    resource: 'iron',
    job: Job.Miner,
    /** Sparser again than stone, and the poorest load in the game. See above. */
    amount: 96,
    lattice: 9,
    load: 1.5,
  },
];

export function nodeKind(kind: number): NodeKindDef {
  return NODE_KINDS[kind] ?? NODE_KINDS[0];
}

/** The kind seeded from this terrain, or −1. */
export function nodeKindForTerrain(terrain: number): number {
  for (const k of NODE_KINDS) if (k.terrain === terrain) return k.kind;
  return -1;
}

/**
 * The kind a building with this job works, or −1.
 *
 * Keyed on the **job** rather than on `needsTerrain`, and the difference is a
 * real one rather than tidiness. The Verdant Choir's Sunken Grove also stands on
 * `Terrain.Forest` and also produces timber, and it is a *grove*: it grows, it
 * is the Choir's housing, and a grove that felled the wood it was grown in would
 * eat its own people's beds. Its job is `Farmer`, so it falls through here and
 * keeps producing exactly as it always did. Only the four buildings whose job is
 * Woodcutter, Quarrier or Miner haul.
 */
export function harvestKind(job: number): number {
  for (const k of NODE_KINDS) if (k.job === job) return k.kind;
  return -1;
}

export function nodeKindById(id: string): number {
  for (const k of NODE_KINDS) if (k.id === id) return k.kind;
  return -1;
}
